import './index.css';
import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { StoreContext } from '../../store';

import { lifeStyleFeatures, walkIndexFeatures } from '../../constants';

import Show from '../../utility/Show';
import MapQuickSearch from '../EstablishmentSearch/map-quick-search';
import AddressInputs from '../CompareMaps/address-inputs';
import { WalkingLoader } from '../Loaders/index.jsx';
import Profile from '../Profile';
import { Modal } from '../Modal/Modal.jsx';

import ExploreMap from './bg-map.jsx';
import BackButton from './back-btn.jsx';
import WalkingRadius from './walk-radius.jsx';
import NeighborhoodReports from './reports.jsx';

import WalkSummary from '../DashboardSimplified/index.jsx';

import { debounce } from '../../services.js';

import { isObjectEmpty } from '../../services.js';

const Explore = () => {
  const { state, dispatch, actions, navigate } = useContext(StoreContext);
  const { currentAddress, isGoogleMapsLoaded, listOfProperties, isMobile, isEmbedMode } = state;
  const [mapCenter, setMapCenter] = useState(null);
  const [title, setTitle] = useState('');
  const [showSingleInterface, setShowSingleInterface] = useState(false);
  const [showCategoryFeatures, setShowCategoryFeatures] = useState(false);
  const [neighborhoodFeature, setNeighborhoodFeature] = useState('lifestyle');
  const [selectedGemFeature, setSelectedGemFeature] = useState(null);
  const [showWalkingRadius, setShowWalkingRadius] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState(0);
  const [isMapQuickSearchOpen, setIsMapQuickSearchOpen] = useState(false);
  const [showRelevantFilters, setShowRelevantFilters] = useState(false);
  const [showCompareSearch, setShowCompareSearch] = useState(false);
  const [showNeighborhoodReports, setShowNeighborhoodReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const properties = listOfProperties.map((item) => {
    return {
      label: item.property,
      value: item,
    };
  });

  useEffect(() => {
    dispatch({ type: 'SET_IS_EXPLORE_ENABLED', payload: true });
  }, []);

  useEffect(() => {
    const trackAddress = async () => {
      if (!isObjectEmpty(currentAddress) && isGoogleMapsLoaded) {
        await actions.handleUsageTracker(
          'address',
          currentAddress?.property,
          currentAddress,
          false,
          true,
        );
      }
    };
    trackAddress();
  }, [currentAddress]);

  useEffect(() => {
    if (currentAddress && isGoogleMapsLoaded) {
      setMapCenter({
        lat: parseFloat(currentAddress.latitude),
        lng: parseFloat(currentAddress.longitude),
      });
    }
  }, [currentAddress, isGoogleMapsLoaded]);

  const appFeatures = useMemo(() => [
    { label: "show dashboard", isClickable: true, onClick: () => setShowSingleInterface(true) },
    { label: "search must-haves", isClickable: true, onClick: () => setIsMapQuickSearchOpen(true) },
    { label: "compare neighborhoods", isClickable: true, onClick: () => setShowCompareSearch(true) },
    { label: "lifestyle essentials", isClickable: true, onClick: () => handleCategoryClick('lifestyle') },
    { label: "sidewalk character", isClickable: true, onClick: () => handleCategoryClick('character') },
    // { label: "quick multiple search", isClickable: true, onClick: () => setIsMapQuickSearchOpen(true) },
    // { label: "relevant filters", isClickable: true, onClick: () => setShowRelevantFilters(true) },
    // { label: "neighborhood reports", isClickable: true, onClick: () => setShowNeighborhoodReports(true) },
    // { label: "analytics", isClickable: true, onClick: () => setShowAnalytics(true) },
  ], []);

  const gemFilters = useMemo(() => [
    {
      feature: 'food',
      filtersTitle: ['Grocery', 'Restaurants', 'Fast Foods', 'Cafe', 'Bars'],
    },
    {
      feature: 'services',
      filtersTitle: ['Education', 'Health', 'Business', 'Finance', 'Safety', 'Gas Station'],
    },
    {
      feature: 'shop',
      filtersTitle: ['Big Box', 'Beauty', 'Household', 'Other Shops'],
    },
    {
      feature: 'transit',
      filtersTitle: ['Bus', 'Subway', 'Train', 'Ferry', 'Terminal'],
    },
    {
      feature: 'leisure',
      filtersTitle: ['Dog Park', 'Cinema', 'Threater', 'Concert', 'Park', 'Fitness'],
    },
  ], []);

  const mapStyle = useMemo(() => ({
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
      { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
      { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
    ]
  }), []);

  const notInExporeMaster = useCallback(() =>
    showCategoryFeatures ||
    showWalkingRadius ||
    selectedGemFeature !== null ||
    isMapQuickSearchOpen ||
    showRelevantFilters ||
    showCompareSearch ||
    showNeighborhoodReports ||
    selectedReport !== null
  , [
    showCategoryFeatures, showWalkingRadius,
    selectedGemFeature, isMapQuickSearchOpen, showRelevantFilters,
    showCompareSearch, showNeighborhoodReports, selectedReport
  ]);

  const hasPopup = useCallback(() =>
    showSingleInterface || showAnalytics || showMap,
  [showSingleInterface, showAnalytics, showMap]);

  const handleBack = useCallback(() => {
    setShowSingleInterface(false);
    setShowCategoryFeatures(false);
    setSelectedGemFeature(null);
    setShowWalkingRadius(false);
    setSelectedRadius(0);
    setIsMapQuickSearchOpen(false);
    setShowRelevantFilters(false);
    setShowCompareSearch(false);
    setShowNeighborhoodReports(false);
    setSelectedReport(null);
    setShowAnalytics(false);
    setShowMap(false);
    setTitle('');
    dispatch({ type: 'SET_SHOW_FILTERS', payload: false });
    dispatch({ type: 'RESET_CATEGORY', payload: [''] });
    dispatch({ type: 'SET_SHOW_MAP_QS', payload: false });
    dispatch({ type: 'SET_CURRENT_TAB', payload: 'lifestyle' });
    dispatch({ type: 'SET_SHOW_NEIGHBORHOOD_BOUNDS', payload: false });
    dispatch({ type: 'SET_SHOW_CITY_BOUNDS', payload: false });
    dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: false });
  }, []);

  const handleCategoryClick = useCallback((category) => {
    setNeighborhoodFeature(category);
    setShowCategoryFeatures(true);
  }, []);

  const getTitle = useCallback(() => (
    <div className='text-white text-xl px-6 py-2 border-0 border-b border-b-white uppercase slide-in-right'>
      <Show>
        <Show.When isTrue={selectedGemFeature !== null && neighborhoodFeature === 'lifestyle'}>
          <div className="title flex gap-2 items-center gap-4 cursor-pointer">
            <div>{selectedGemFeature?.svg}</div>
            <div>{selectedGemFeature?.name}</div>
          </div>
        </Show.When>
        <Show.When isTrue={selectedReport !== null}>
          {selectedReport}
        </Show.When>
        <Show.Else>
          {title}
        </Show.Else>
      </Show>
    </div>
  ), [selectedGemFeature, neighborhoodFeature, selectedReport, title]);

  const isLifestyle = neighborhoodFeature === 'lifestyle';

  const handleMapFeature = async (value) => {
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    setShowMap(true);
    dispatch({
      type: 'SET_CURRENT_TAB',
      payload: isLifestyle ? 'lifestyle' : 'walkindex',
    });

    dispatch({
      type: 'SET_MAP_TAB',
      payload: isLifestyle ? selectedGemFeature?.name : value,
    });

    if (isLifestyle) {
      debounce(() => {
        dispatch({ type: 'SET_SHOW_FILTERS', payload: true });
        dispatch({ type: 'SET_SELECTED_EXPLORE_ESSENTIAL', payload: value });
      }, 1000)();
    } else {
      await actions.handleUsageTracker('charactercategory', value, currentAddress);
    }
  };

  const handleWalkingRadius = (distance) => {
    // dispatch({ type: 'SET_CURRENT_ZOOM', payload: getZoom(distance) });
    // setSelectedRadius(distance);
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    setShowMap(true);
    dispatch({ type: 'SET_CURRENT_DISTANCE', payload: distance });
  };

  const handleMapReport = (report) => {
    if (report === 'Neighborhood Map') {
      dispatch({ type: 'SET_SHOW_NEIGHBORHOOD_BOUNDS', payload: true });
    } else {
      dispatch({ type: 'SET_SHOW_CITY_BOUNDS', payload: true });
    }

    setShowMap(true);
  };

  const handleCompareSearch = () => {
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: true });
    setShowCompareSearch(false);
    setShowMap(true);
  };

  const handleQuickSearch = () => {
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    dispatch({ type: 'SET_SHOW_MAP_QS', payload: true });
    setShowMap(true);
  }

  const categoryFeatureList = useCallback(() => {
    const selectedFeatures = isLifestyle
      ? lifeStyleFeatures.slice(1, lifeStyleFeatures.length - 1)
      : walkIndexFeatures;

    const selectedFilters = gemFilters
      .find(
        filter => filter.feature.toLowerCase() === selectedGemFeature?.name?.toLowerCase()
      ) || {};

    return (
      <div className='w-full slide-in-right'>
        <Show>
          <Show.When isTrue={selectedGemFeature !== null && neighborhoodFeature === 'lifestyle'}>
            <div className='slide-in-right'>
              {selectedFilters?.filtersTitle?.map((filter, filterIndex) => (
                <div
                  key={filterIndex}
                  className="category-feature text-lg border-0 border-b border-b-white px-14 py-4 uppercase text-white cursor-pointer"
                  style={{ '--feature-color': selectedGemFeature.color }}
                  onClick={() => handleMapFeature(filter)}
                >
                  {filter}
                </div>
              ))}
            </div>
          </Show.When>
          <Show.Else>
            {selectedFeatures.map((feature, index) => (
              <div
                key={index}
                className="category-feature text-lg border-0 border-b border-b-white px-6 py-4 uppercase text-white"
                style={{ '--feature-color': feature.color }}
              >
                <div
                  className="category-feature grid grid-cols-12 items-center gap-4 cursor-pointer"
                  onClick={() => isLifestyle ? setSelectedGemFeature(feature) : handleMapFeature(feature.name)}
                >
                  {/* <div className='col-span-2'>{feature.svg}</div> */}
                  <div className='col-span-10'>{feature.name}</div>
                </div>
              </div>
            ))}
          </Show.Else>
        </Show>
      </div>
    );
  }, [neighborhoodFeature, selectedGemFeature, gemFilters, isLifestyle]);

  const relevantFilters = useCallback(() => {
    const filters = [
      'Grocery', 'Restaurants', 'Fast Food', 'Cafes', 'Bars', 'Big Box', 'Beauty', 'Household', 'Other Shops',
      'Education', 'Health', 'Business', 'Finance', 'Safety', 'Gas Station', 'Transit', 'Leisure', 'Others'
    ];

    return (
      <div className='w-full slide-in-right max-h-[400px] overflow-y-auto custom-scrollbar'>
        {filters.map((filter, index) => (
          <div
            className='text-lg border-0 border-b border-b-white px-14 py-4 uppercase text-white hover:text-[#ff914d] cursor-pointer'
            key={index}
          >
            {filter}
          </div>
        ))}
      </div>
    );
  }, []);

  const handleRedirectToMap = (value) => {
    setShowMap(value);
    setShowSingleInterface(false);
  };

  const openEstablishmentSearch = () => {
    if (isMobile || isEmbedMode) actions.toggleFullScreenMap();
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    dispatch({ type: 'SET_SHOW_MAP_QS', payload: true });
    handleRedirectToMap(true);
  };

  return (
    <div className={`explore-container relative h-full w-full flex items-center justify-start md:justify-center ${
      !notInExporeMaster() ? 'open-explore-master' : ''
    } ${
      showCompareSearch ? 'show-explore-compare-search' : ''
    }`}
    >
      <Show.When isTrue={!currentAddress || mapCenter === null || !isGoogleMapsLoaded}>
        <WalkingLoader blurValue='2px' />
      </Show.When>
      <Show.When isTrue={currentAddress && mapCenter !== null && isGoogleMapsLoaded}>
        <Show>
          <Show.When isTrue={hasPopup()}>
            <div className='absolute w-full h-full flex flex-col items-center justify-normal lg:justify-center slide-in-right'>
              <div className={`bg-white z-10 w-full ${
                  showMap ? 'h-full' : 'max-h-[850px]'
                } ${
                  showSingleInterface ? 'max-w-[1275px]' : ''
                }`}
              >
                <Show.When isTrue={showAnalytics}>
                  <Profile />
                </Show.When>
                <Show.When isTrue={showSingleInterface}>
                  <WalkSummary setShowMap={handleRedirectToMap} handleBack={handleBack} />
                </Show.When>
                <Show.When isTrue={showMap}>
                  <Modal key={'explore-map-container'} backExplore={handleBack} />
                </Show.When>
              </div>
            </div>
          </Show.When>
          <Show.Else>
            <div className='explore-sidebar absolute left-8 top-1/2 -translate-y-1/2 bg-[#3a3e49] px-8 pt-5 pb-10 w-[400px] z-10'>
              {notInExporeMaster() && (
                <>
                  <BackButton
                    onClick={handleBack}
                    color="white"
                    text="Back to Explore"
                    className='pt-0 !pl-0'
                  />
                  {getTitle()}
                </>
              )}
              <Show>
                <Show.When isTrue={showCategoryFeatures}>
                  {categoryFeatureList()}
                </Show.When>
                <Show.When isTrue={showWalkingRadius}>
                  <WalkingRadius
                    selectedRadius={selectedRadius}
                    setSelectedRadius={handleWalkingRadius}
                  />
                </Show.When>
                <Show.When isTrue={isMapQuickSearchOpen}>
                  <div className='slide-in-right pt-4'>
                    <MapQuickSearch showResults={false} className='!bg-transparent pb-4' defaultInput={5} />
                    <div
                      className='border border-white w-fit text-white text-sm ml-auto px-4 py-1 text-center cursor-pointer'
                      onClick={() => handleQuickSearch()}
                    >
                      Search
                    </div>
                  </div>
                </Show.When>
                <Show.When isTrue={showRelevantFilters}>
                  {relevantFilters()}
                </Show.When>
                <Show.When isTrue={showCompareSearch}>
                  <div className='w-full slide-in-right pt-4'>
                    <AddressInputs state={state} dispatch={dispatch} />
                    <div
                      className='border border-white w-fit text-white text-sm ml-auto px-4 py-1 text-center cursor-pointer'
                      onClick={() => handleCompareSearch()}
                    >
                      Search
                    </div>
                  </div>
                </Show.When>
                <Show.When isTrue={showNeighborhoodReports}>
                  <NeighborhoodReports
                    selectedReport={selectedReport}
                    setSelectedReport={setSelectedReport}
                    handleMapReport={handleMapReport}
                  />
                </Show.When>
                <Show.Else>
                  <div className='flex flex-col gap-4 slide-in-left'>
                    <BackButton
                      onClick={() => navigate('/home')}
                      color="#ff914d"
                      text="Back to Home"
                      className='!p-0'
                    />
                    <div className='text-white text-xl md:text-[27px] text-center font-medium pt-0 md:pt-5'>
                      EXPLORE THE NEIGHBORHOOD
                    </div>
                    <div className='text-lg text-white text-center'>{currentAddress?.property}</div>
                    <div>
                      {appFeatures.map((feature, index) => (
                        <div
                          key={index}
                          className={`text-md md:text-lg border-0 border-b border-t-white p-4 md:p-6 uppercase ${
                            feature.isClickable ? 'text-white cursor-pointer hover:text-[#ff914d]' : 'text-black'
                          }`}
                          onClick={
                            feature.isClickable && feature.onClick
                              ? () => {
                                  feature.onClick();
                                  setTitle(feature.label);
                                }
                              : undefined
                          }
                        >
                          {feature.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </Show.Else>
              </Show>
            </div>
          </Show.Else>
        </Show>
        <div className={`relative h-full w-full ${
            hasPopup() ? 'hidden' : ''
          }`}
        >
          <ExploreMap mapCenter={mapCenter} mapStyle={mapStyle} hasPopup={hasPopup()} />
        </div>
      </Show.When>
    </div>
  );
};

export default Explore;